import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CopilotEditTarget, DirtyCheckable, EditSessionRegistrar } from './edit-session';

/**
 * Every open edit session, so the code that must ask about one does not have to
 * find the page that owns it.
 *
 * Three callers, all of them outside the page:
 *
 *  - `UnsavedChangesGuard` — navigating away inside the app.
 *  - `beforeunload` — closing the tab or reloading, where the router never runs.
 *  - the app-level **agent composer**, which sits outside the router outlet but
 *    must propose work into the routed page's own session.
 *
 * A session registers itself (`EditSessionConfig.registry`) and hands back an
 * unregister closure, so the registry never holds a page alive: a page that
 * forgets to call `destroy()` leaks one entry that reports its own dirtiness,
 * not a component tree.
 *
 * The `beforeunload` listener is attached once, on first registration, and
 * removed when the last session goes — an always-attached handler on a service
 * that most pages never touch would make every page slower to leave.
 */
@Injectable({ providedIn: 'root' })
export class EditSessionRegistry implements EditSessionRegistrar, OnDestroy {
  private readonly sessions = new Set<DirtyCheckable>();

  /**
   * The session the Copilot is currently addressing, or null when the page on
   * screen has none.
   *
   * Separate from `sessions` on purpose. `sessions` is a SET, because "is
   * anything dirty?" is a question about all of them at once; the Copilot's
   * target is exactly one, because a proposal has to land somewhere definite —
   * with two workspace pages briefly alive across a navigation, "any of them"
   * is not an answer.
   */
  private readonly copilotTargetSubject = new BehaviorSubject<CopilotEditTarget | null>(null);
  readonly copilotTarget$ = this.copilotTargetSubject.asObservable();

  get copilotTarget(): CopilotEditTarget | null { return this.copilotTargetSubject.value; }

  /**
   * Offer this page's session to the Copilot. Returns a closure that withdraws
   * it — call it from `ngOnDestroy`.
   *
   * The withdrawal only clears the target if it is still this one: page B can
   * be created before page A is destroyed, and A's teardown must not take B's
   * offer down with it.
   */
  setCopilotTarget(target: CopilotEditTarget): () => void {
    this.copilotTargetSubject.next(target);
    return () => {
      if (this.copilotTargetSubject.value === target) this.copilotTargetSubject.next(null);
    };
  }

  /**
   * Push one Copilot proposal at whatever page is on screen. False means it did
   * not land — there is no page with a session, or the page did not recognise
   * the operation — and the caller must say so rather than assume an edit.
   */
  propose(op: Parameters<CopilotEditTarget['propose']>[0]): boolean {
    return this.copilotTargetSubject.value?.propose(op) ?? false;
  }

  private readonly onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!this.dirty) return;
    // The modern spelling and the legacy one: browsers disagree about which
    // triggers the prompt, and neither is expensive.
    e.preventDefault();
    e.returnValue = '';
  };

  /** Track a session. The returned function stops tracking it. */
  register(session: DirtyCheckable): () => void {
    if (!this.sessions.size) window.addEventListener('beforeunload', this.onBeforeUnload);
    this.sessions.add(session);
    return () => {
      this.sessions.delete(session);
      if (!this.sessions.size) window.removeEventListener('beforeunload', this.onBeforeUnload);
    };
  }

  /** True when any open session holds unsaved work. */
  get dirty(): boolean {
    for (const session of this.sessions) if (session.dirty) return true;
    return false;
  }

  /** How many sessions are open — for tests and diagnostics, not for UI. */
  get size(): number { return this.sessions.size; }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.sessions.clear();
    this.copilotTargetSubject.next(null);
  }
}
